import { ReactElement, useContext, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  SecondmentActor,
  SecondmentCatalogResult,
  SecondmentDetail,
  getSecondmentCatalog,
  getSecondmentDetail,
} from "../../Services/PublicSecondments";
import AuthContext from "../../store/AuthContext";
import { IPerson } from "../../interfaces/models/IPerson";
import "../../assets/css/PageSurface.css";
import "../../assets/css/secondments.css";
import SecondmentsApply from "./components/SecondmentsApply";
import SecondmentsIntro from "./components/SecondmentsIntro";
import SecondmentsList from "./components/SecondmentsList";
import SecondmentsOverview from "./components/SecondmentsOverview";

function sessionTokenPresent(): boolean {
  if (sessionStorage.getItem("token")) {
    return true;
  }
  return document.cookie.split(";").some((part) => part.trim().startsWith("token="));
}

function phoneText(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (!value || typeof value !== "object") {
    return "";
  }
  const row = value as { code?: unknown; phone?: unknown };
  const code = typeof row.code === "string" ? row.code.trim() : "";
  const phone = typeof row.phone === "string" ? row.phone.trim() : "";
  return [code, phone].filter((part) => part.length > 0).join(" ");
}

function actorFromPerson(person: IPerson): SecondmentActor {
  const title = person.listTitle || person.unlistedTitle || String(person.title || "");
  return {
    contactId: String(person.legacyId || ""),
    companyId: String(person.firmLegacyId || ""),
    name: `${person.name || ""} ${person.lastName || ""}`.trim(),
    email: person.firmEmail || "",
    phone: phoneText(person.directPhoneNumber) || phoneText(person.phoneNumber),
    firmName: person.firmName || "",
    title,
  };
}

type LoadedCatalog = {
  result: SecondmentCatalogResult;
  forSearch: string;
};

function catalogQuery(params: URLSearchParams, companyId: string): Record<string, string> {
  const query: Record<string, string> = {};
  ["words", "firm", "jurisdiction", "region", "page", "t"].forEach((key) => {
    const value = params.get(key);
    if (value) {
      query[key] = value;
    }
  });
  if (companyId) {
    query.companyId = companyId;
  }
  return query;
}

export default function SecondmentsPage(): ReactElement {
  const [params, setSearchParams] = useSearchParams();
  const { loggedPerson } = useContext(AuthContext);
  const action = params.get("action") || "";
  const search = params.toString();
  const isDetail = action === "view" || action === "app" || action === "info";
  const [loadedCatalog, setLoadedCatalog] = useState<LoadedCatalog | null>(null);
  const [detail, setDetail] = useState<SecondmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const actor = loggedPerson ? actorFromPerson(loggedPerson) : null;
  // A catalog belongs to the query that loaded it. Using a stale redirect on Back
  // would replace the list history entry and skip the secondments listing.
  const catalog = loadedCatalog?.forSearch === search ? loadedCatalog.result : null;
  const listLoading = loading || (loadedCatalog?.forSearch !== search && error === "");

  useEffect(() => {
    if (!sessionTokenPresent()) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    const current = new URLSearchParams(search);
    const load = isDetail
      ? getSecondmentDetail(current.get("secid") || "0", current.get("firm") || "", current.get("view") === "1", actor?.contactId || "", actor?.companyId || "").then((row) => {
        if (!cancelled) {
          setDetail(row);
        }
      })
      : getSecondmentCatalog(catalogQuery(current, actor?.companyId || "")).then((row) => {
        if (!cancelled) {
          setLoadedCatalog({ result: row, forSearch: search });
        }
      });
    load.catch(() => {
      if (!cancelled) {
        setError("The secondment list could not be loaded.");
      }
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isDetail, search, actor?.contactId, actor?.companyId]);

  useEffect(() => {
    if (isDetail || !catalog?.redirect) {
      return;
    }
    const next = new URLSearchParams();
    next.set("action", "view");
    next.set("view", "1");
    next.set("secid", String(catalog.redirect.secId));
    if (catalog.redirect.firmId) {
      next.set("firm", String(catalog.redirect.firmId));
    }
    setSearchParams(next, { replace: true });
  }, [catalog, isDetail, setSearchParams]);

  if (!sessionTokenPresent()) {
    return <Navigate to="/login?legacyUrl=/secondments" replace />;
  }

  return (
    <div className="page-sheet">
      <SecondmentsIntro />
      {isDetail && loading ? <p>Loading secondments...</p> : null}
      {isDetail && error ? <p className="secondments-error">{error}</p> : null}
      {action === "view" && detail ? <SecondmentsOverview detail={detail} /> : null}
      {(action === "app" || action === "info") && detail && actor ? (
        <SecondmentsApply detail={detail} actor={actor} mode={action} applicantPlace={detail.applicantPlace} />
      ) : null}
      {(action === "app" || action === "info") && detail && !actor ? <p>Sign in to contact the firm.</p> : null}
      {!isDetail ? <SecondmentsList catalog={catalog} loading={listLoading} error={error} /> : null}
    </div>
  );
}
